import React from "react";
import {
  useWSModalCustom,
  WSControl,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSInfoBox,
  WSInputTextarea,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IRequirementResponse,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import {
  ContextItem,
  RequirementAdditionalData,
  RequirementStatusViewProps
} from "../../types";
import { useUpdatePayeeEngagementRequirement } from "../../../../query/payeeEngagements/mutations/useUpdatePayeeEngagementRequirement";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { usePayeeRowsSummaryQuery } from "../../../../query/search/payee/queries/usePayeeRowsSummaryQuery";
import { useResetPayeeRequirement } from "../../../../query/requirements/mutations/useResetPayeeRequirement";
import { RequirementReviewStrategy } from "@wingspanhq/payments/dist/interfaces/requirement";

interface MarkRequirementAsCompleteModalProps {
  onClose: () => void;
  requirement: IRequirementResponse;
  context: ContextItem;
  additionalData?: RequirementAdditionalData;
  currentStatus: RequirementStatusViewProps;
  onSuccess: () => void;
}

type FormData = {
  reason: string;
  reRequest: boolean;
};

const MarkRequirementAsIncompleteModal: React.FC<
  MarkRequirementAsCompleteModalProps
> = ({ onClose, requirement, context, onSuccess, currentStatus }) => {
  const [changeRequirementStatus, changeRequirementStatusMeta] =
    useUpdatePayeeEngagementRequirement();

  const [resetRequirement, resetRequirementMeta] = useResetPayeeRequirement();

  const queryPayeeGeneralSummary = usePayeeRowsSummaryQuery({
    filter: {
      "engagements.requirements.requirementId": requirement.requirementId
    }
  });

  const onSubmit = (formData: FormData) => {
    changeRequirementStatus(
      {
        payeeId: requirement.payeeId!,
        requirementId: requirement.requirementId,
        engagementId: requirement.payerPayeeEngagementIds[0]!,
        request: {
          status: RequirementStatus.Incomplete,
          reason: formData.reason
        }
      },
      {
        onSuccess() {
          if (formData.reRequest) {
            resetRequirement(
              {
                payeeId: requirement.payeeId || "",
                requirementId: requirement.requirementId
              },
              {
                onSuccess() {
                  onSuccess();
                  onClose();
                }
              }
            );
          } else {
            onSuccess();
            onClose();
          }
        }
      }
    );
  };

  const contractorsLength = queryPayeeGeneralSummary.data?.listSize || "-";

  const isApprovalFlow =
    requirement.reviewStrategy === RequirementReviewStrategy.AlwaysRequired ||
    requirement.reviewStrategy === RequirementReviewStrategy.OnFail;

  return (
    <WSForm<FormData> onSubmit={onSubmit} defaultValues={{ reason: "" }}>
      <WSModal
        title={
          isApprovalFlow
            ? "Reject requirement"
            : "Mark requirement as incomplete"
        }
        onClose={onClose}
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onClose,
              type: "button"
            },
            {
              kind: "Primary",
              label: isApprovalFlow ? "Yes, reject" : "Yes, mark incomplete",
              type: "submit",
              loading:
                changeRequirementStatusMeta.isLoading ||
                resetRequirementMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="XL">
          <WSFlexBox wrap="nowrap" gap="M">
            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Current status:
              </WSText.ParagraphSm>
              <WSPill {...currentStatus.pill}></WSPill>
              <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                {currentStatus.secondaryText}
              </WSText.ParagraphXs>
            </WSElement>
            <WSIcon
              style={{
                marginTop: "auto",
                marginBottom: "auto"
              }}
              name="arrow-right"
            />
            <WSForm.Value name="reRequest">
              {(isReRequest: boolean) => (
                <WSElement>
                  <WSText.ParagraphSm color="gray600" mb="S">
                    Overwritten status preview:
                  </WSText.ParagraphSm>
                  <WSPill
                    text="Awaiting contractor"
                    theme="neutral"
                    icon="time"
                  />
                  <WSText.ParagraphXs mt="XS" ml="S" color="gray400">
                    {isReRequest
                      ? "Awaiting contractor completion"
                      : isApprovalFlow
                      ? "Rejected by you"
                      : "Marked incomplete by you"}
                  </WSText.ParagraphXs>
                </WSElement>
              )}
            </WSForm.Value>
          </WSFlexBox>

          <WSList gap="L">
            <WSElement>
              <WSText.ParagraphSm color="gray500">
                Re-request submission (optional)
              </WSText.ParagraphSm>
              <WSText.ParagraphXs color="gray400" mt="XS" mb="S">
                Check the box to reset the status and request a new submission
                by the contractor.
              </WSText.ParagraphXs>
              <WSForm.Field
                label="Re-request submission"
                name="reRequest"
                component={WSControl}
                componentProps={{
                  type: "checkbox"
                }}
              />
            </WSElement>

            <WSElement>
              <WSForm.Field
                label="Comment (optional)"
                name="reason"
                component={WSInputTextarea}
                componentProps={{
                  helperText:
                    "Please provide specific details that will inform the contractor about the rejection",
                  placeholder: "Provide specific details about the decision"
                }}
              />
            </WSElement>
          </WSList>

          <WSList gap="L">
            <WSInfoBox>
              <WSText.ParagraphSm color="gray600">
                By proceeding, the requirement status will be marked incomplete
                by you.
                <ul>
                  <li>
                    The status will be updated to “Awaiting contractor”
                    (requiring contractor action)
                  </li>
                  <li>
                    Optionally, you can re-request the contractor to take action
                    once again to fulfill the requirement (such as upload a new
                    file)
                  </li>
                  <li>
                    Provide a reason and comments to the contractor regarding
                    this decision
                  </li>
                  <li>
                    The decision and status updates will be tracked in the
                    requirement history
                  </li>
                </ul>
              </WSText.ParagraphSm>
              <WSText.ParagraphSm color="gray600">
                This requirement is assigned to {contractorsLength} of the
                contractor’s engagements and may affect their eligibility to
                receive payments from you. Would you like to proceed?
              </WSText.ParagraphSm>
            </WSInfoBox>
            <WSInfoBox>
              <WSText.ParagraphSm color="gray600">
                If you are looking to stop working with the contractor on an
                engagement or stop working with the contractor entirely and
                disregard the requirement(s) altogether, go to the contractor
                profile and archive the contractor engagement or the contractor
                entirely.
              </WSText.ParagraphSm>
            </WSInfoBox>
          </WSList>
          <WSErrorMessage
            mt="XL"
            contextKey="Other"
            error={changeRequirementStatusMeta.isError}
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useMarkRequirementAsIncompleteModal = () =>
  useWSModalCustom(MarkRequirementAsIncompleteModal);
